import { getLogger } from "@core/logging/logger";
import { NewPasswordNeededError, UserInMultipleInfractureError } from "@domain/authentication/authentication-errors";
import {
	CognitoError,
	CognitoErrorCode,
	CognitoErrorMessage,
	isCognitoError,
} from "@domain/authentication/cognito/cognito-error";
import { isMultiInfraCognitoError } from "@domain/authentication/cognito/cognito-multi-infra-errors";
import { cognitoMultiInfraPromise } from "@domain/authentication/cognito/cognito-promises";
import { CognitoServiceWrapper } from "@domain/authentication/cognito/cognito-service-wrapper";
import { CognitoUserPoolProvider } from "@domain/authentication/cognito/cognito-user-pool-provider";

import { AuthenticationDetails, CognitoUser, CognitoUserSession } from "amazon-cognito-identity-js";

interface MultipleInfraLoginResult {
	cognitoUser: CognitoUser;
	cognitoUserSession: CognitoUserSession;
}

export class CognitoMultiInfra {
	private readonly logger = getLogger("🧠 [CognitoMultiInfra]");

	constructor(
		private readonly cognitoServiceWrapper: CognitoServiceWrapper,
		private readonly userPoolProvider: CognitoUserPoolProvider,
	) {}

	async multipleInfraIsEmailAvailable(email: string): Promise<boolean> {
		const authenticationDetails = new AuthenticationDetails({ Username: email, Password: "" });
		const cognitoUsers = this.userPoolProvider.generateCognitoUsersForAllInfras(email);
		try {
			return await cognitoMultiInfraPromise(
				cognitoUsers.map(async cognitoUser => {
					try {
						await this.cognitoServiceWrapper.authenticateUser(cognitoUser.user, authenticationDetails);
					} catch (e) {
						if (
							!isCognitoError(e) ||
							(e.code !== CognitoErrorCode.NotAuthorizedException &&
								e.code !== CognitoErrorCode.UserNotConfirmedException)
						) {
							throw e;
						}
					}
				}),
				async () => false,
			);
		} catch (e) {
			this.logger.debug("multipleInfraIsEmailAvailable => true.", e);
			return true;
		}
	}

	async multipleInfraForgotPassword(email: string): Promise<void> {
		const cognitoUsers = this.userPoolProvider.generateCognitoUsersForAllInfras(email);

		await cognitoMultiInfraPromise(
			cognitoUsers.map(async cognitoUser => {
				await this.cognitoServiceWrapper.forgotPassword(cognitoUser.user);
				return { ...cognitoUser };
			}),
			async result => await this.userPoolProvider.changeInfrastructure(result.infrastructureId),
			async aggregateError => {
				const errors = aggregateError.errors.map((error: unknown, index) => ({ error, ...cognitoUsers[index] }));
				const cognitoErrors = errors.filter(isMultiInfraCognitoError);

				if (cognitoErrors.every(({ error }) => error.code === CognitoErrorCode.UserNotFoundException)) {
					return cognitoErrors[0].error;
				}

				const limitExceededErrors = cognitoErrors.filter(
					cognitoError => cognitoError.error.code === CognitoErrorCode.LimitExceededException,
				);

				if (limitExceededErrors.length >= 1) {
					return limitExceededErrors[0].error;
				}

				const notAuthorizedErrors = cognitoErrors.filter(
					cognitoError => cognitoError.error.code === CognitoErrorCode.NotAuthorizedException,
				);
				if (notAuthorizedErrors.length > 0) {
					return notAuthorizedErrors[0].error;
				}

				const userNotVerifiedErrors = cognitoErrors.filter(
					({ error }) =>
						error.code === CognitoErrorCode.InvalidParameterException && error.message.includes("verified email"),
				);

				if (userNotVerifiedErrors.length > 1) {
					return new UserInMultipleInfractureError("User cannot have sessions in multiple infras! (case 1)");
				}

				if (userNotVerifiedErrors.length === 1) {
					await this.userPoolProvider.changeInfrastructure(userNotVerifiedErrors[0].infrastructureId);
					return userNotVerifiedErrors[0].error;
				}

				return new Error("Need to work on these cases");
			},
		);
	}

	async multipleInfraLoginEmail(
		email: string,
		password: string,
		updateUser: (user: CognitoUser) => Promise<void>,
	): Promise<MultipleInfraLoginResult> {
		const authenticationDetails = new AuthenticationDetails({ Username: email, Password: password });
		const cognitoUsers = this.userPoolProvider.generateCognitoUsersForAllInfras(email);

		return await cognitoMultiInfraPromise(
			cognitoUsers.map(async cognitoUser => {
				const userSession = await this.cognitoServiceWrapper.authenticateUser(cognitoUser.user, authenticationDetails);
				return { userSession, ...cognitoUser };
			}),
			async result => {
				await this.userPoolProvider.changeInfrastructure(result.infrastructureId);
				return { cognitoUserSession: result.userSession, cognitoUser: result.user };
			},
			async aggregateError => {
				const errors = aggregateError.errors.map((error: unknown, index) => ({ error, ...cognitoUsers[index] }));

				const newPasswordNeededErrors = errors.filter(error => error.error instanceof NewPasswordNeededError);

				if (newPasswordNeededErrors.length > 1) {
					return new UserInMultipleInfractureError("User cannot have sessions in multiple infras! (case 2)");
				}

				if (newPasswordNeededErrors.length === 1) {
					await this.userPoolProvider.changeInfrastructure(newPasswordNeededErrors[0].infrastructureId);
					await updateUser(newPasswordNeededErrors[0].user);
					return newPasswordNeededErrors[0].error as NewPasswordNeededError;
				}

				const cognitoErrors: {
					error: CognitoError;
					infrastructureId: string;
				}[] = [];

				errors.forEach(({ error, infrastructureId }) => {
					if (isCognitoError(error)) cognitoErrors.push({ error, infrastructureId });
				});

				const userNotConfirmedErrors = cognitoErrors.filter(
					cognitoError => cognitoError.error.code === CognitoErrorCode.UserNotConfirmedException,
				);

				if (userNotConfirmedErrors.length > 1) {
					return new UserInMultipleInfractureError("User cannot have sessions in multiple infras! (case 3)");
				}

				if (userNotConfirmedErrors.length === 1) {
					await this.userPoolProvider.changeInfrastructure(userNotConfirmedErrors[0].infrastructureId);
					return userNotConfirmedErrors[0].error;
				}

				const tooManyAttemptsErrors = cognitoErrors.filter(
					cognitoError =>
						cognitoError.error.code === CognitoErrorCode.NotAuthorizedException &&
						cognitoError.error.message === CognitoErrorMessage.PasswordAttemptsExceeded,
				);

				if (tooManyAttemptsErrors.length >= 1) {
					return tooManyAttemptsErrors[0].error;
				}

				const loginFailedErrors = cognitoErrors.filter(
					cognitoError => cognitoError.error.code === CognitoErrorCode.NotAuthorizedException,
				);

				if (loginFailedErrors.length > 1) {
					return new UserInMultipleInfractureError("User cannot have sessions in multiple infras! (case 4)");
				}

				if (loginFailedErrors.length === 1) {
					return loginFailedErrors[0].error;
				}

				if (cognitoErrors.every(error => error.error.code === CognitoErrorCode.UserNotFoundException)) {
					return cognitoErrors[0].error;
				}

				return Error("Need to work on these cases");
			},
		);
	}
}
